/**
 * @component PortalSidebar
 * @description Navigationsleiste fuer das Kunden-Portal (rolle='kunde').
 * Zeigt Portal-spezifische Menue-Eintraege (Dashboard, Forecasts,
 * Rechnungen, Performance) mit Collapse-Funktion und Logout.
 *
 * @prop {boolean}  collapsed - Ob die Sidebar eingeklappt ist
 * @prop {function} onToggle  - Callback zum Umschalten des collapsed-Status
 * @usage Eingebunden in App.jsx fuer Benutzer mit Rolle 'kunde'
 */
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { IconHome, IconForecast, IconRechnung, IconProfil, IconLogout, IconCollapse, IconExpand } from './Icons';
import { ShieldAlert, TrendingUp, MapPin, Handshake } from 'lucide-react';

const portalItems = [
  { path: '/portal',              tKey: 'customerPortal.sidebar.dashboard',    Icon: IconHome },
  { path: '/portal/forecasts',    tKey: 'customerPortal.sidebar.forecasts',    Icon: IconForecast },
  { path: '/portal/rechnungen',   tKey: 'customerPortal.sidebar.invoices',     Icon: IconRechnung },
  { path: '/portal/statistics',   tKey: 'portal.stats.sidebarTitle',          Icon: ({ ...p }) => <TrendingUp width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.5} {...p} /> },
  { path: '/portal/address-clarifications', tKey: 'portal.addressClarifications.title', Icon: ({ ...p }) => <MapPin width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.5} {...p} /> },
  { path: '/portal/claims',       tKey: 'portal.claims',                      Icon: ({ ...p }) => <ShieldAlert width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.5} {...p} /> },
  { path: '/portal/vereinbarungen', tKey: 'customerPortal.sidebar.commitments', Icon: ({ ...p }) => <Handshake width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.5} {...p} /> },
];

function NavItem({ item, collapsed, t }) {
  const { path, tKey, Icon } = item;
  const label = t(tKey);
  return (
    <NavLink
      to={path}
      end
      className={({ isActive }) =>
        `group flex items-center gap-3 pl-[13px] pr-4 py-[10px] mx-3 my-[1px] border-l-[3px] rounded-[10px] transition-all duration-150 relative ${
          isActive
            ? 'text-[var(--sidebar-active)] font-semibold border-[var(--sidebar-active)]'
            : 'text-[var(--sidebar-text)] border-transparent hover:text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)]'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className={`flex-shrink-0 ${isActive ? 'text-[var(--sidebar-active)]' : 'text-[var(--sidebar-category)]'}`}>
            <Icon />
          </span>
          {!collapsed && (
            <span className="text-[14px] truncate">{label}</span>
          )}
          {collapsed && (
            <div className="fixed left-16 ml-2 px-2.5 py-1.5 rounded-[10px] bg-[var(--bg-elevated)] border border-[var(--border-card)] text-xs text-[var(--text-primary)] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-dropdown">
              {label}
            </div>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function PortalSidebar({ collapsed, setCollapsed, kundeName }) {
  const { logout } = useAuth();
  const { t } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  return (
    <aside
      className={`${collapsed ? 'w-[64px]' : 'w-[260px]'} bg-[var(--sidebar-bg)] border-r border-[var(--sidebar-border)] flex flex-col transition-all duration-200 flex-shrink-0`}
    >
      {/* Logo / Kundenname */}
      <div className="h-14 flex items-center px-4 border-b border-[var(--sidebar-border)]">
        {collapsed ? (
          <div className="w-8 h-8 rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-gradient)' }}>
            <span className="text-white font-bold text-sm">W</span>
          </div>
        ) : (
          <img src={theme === 'dark' ? '/weship-logo-light.png' : '/weship-logo.png'} alt="WeShip" className="h-7" />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 pt-4 space-y-1 overflow-y-auto">
        {portalItems.map(item => (
          <NavItem key={item.path} item={item} collapsed={collapsed} t={t} />
        ))}
      </nav>

      {/* Bottom: Profil + Logout + Theme + Pin */}
      <div className="pb-3 border-t border-[var(--sidebar-border)] pt-2 space-y-[2px]">
        <NavLink
          to="/profil"
          className={({ isActive }) =>
            `group flex items-center gap-3 pl-[13px] pr-4 py-[10px] mx-3 my-[1px] border-l-[3px] rounded-[10px] transition-colors relative ${
              isActive
                ? 'text-[var(--sidebar-active)] font-semibold border-[var(--sidebar-active)]'
                : 'text-[var(--sidebar-text)] border-transparent hover:text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)]'
            }`
          }
        >
          <span className="flex-shrink-0"><IconProfil /></span>
          {!collapsed && <span className="text-[14px] font-medium">{t('customerPortal.sidebar.profile')}</span>}
          {collapsed && (
            <div className="fixed left-16 ml-2 px-2.5 py-1.5 rounded-[10px] bg-[var(--bg-elevated)] border border-[var(--border-card)] text-xs text-[var(--text-primary)] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-dropdown">
              {t('customerPortal.sidebar.profile')}
            </div>
          )}
        </NavLink>

        <button
          onClick={logout}
          className="group w-full flex items-center gap-3 pl-[16px] pr-4 py-[10px] mx-3 rounded-[10px] text-[var(--sidebar-text)] hover:text-[var(--sidebar-logout-hover-text)] hover:bg-[var(--sidebar-logout-hover-bg)] transition-colors relative"
        >
          <span className="flex-shrink-0"><IconLogout /></span>
          {!collapsed && <span className="text-[14px] font-medium">{t('customerPortal.sidebar.logout')}</span>}
          {collapsed && (
            <div className="fixed left-16 ml-2 px-2.5 py-1.5 rounded-[10px] bg-[var(--bg-elevated)] border border-[var(--border-card)] text-xs text-[var(--text-primary)] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-dropdown">
              {t('customerPortal.sidebar.logout')}
            </div>
          )}
        </button>

        <button
          onClick={() => {
            const next = theme === 'dark' ? 'light' : 'dark';
            toggleTheme();
            fetch(`${import.meta.env.VITE_API_URL || ''}/api/auth/theme`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
              body: JSON.stringify({ theme: next }),
            }).catch(() => {});
          }}
          className="group w-full flex items-center gap-3 pl-[16px] pr-4 py-[10px] mx-3 rounded-[10px] text-[var(--sidebar-text)] hover:text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)] transition-colors relative"
        >
          {theme === 'dark' ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
          )}
          {!collapsed && <span className="text-[14px] font-medium">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
          {collapsed && (
            <div className="fixed left-16 ml-2 px-2.5 py-1.5 rounded-[10px] bg-[var(--bg-elevated)] border border-[var(--border-card)] text-xs text-[var(--text-primary)] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-dropdown">
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </div>
          )}
        </button>

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="group w-full flex items-center gap-3 pl-[16px] pr-4 py-[10px] mx-3 rounded-[10px] text-[var(--sidebar-text)] hover:text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)] transition-colors relative"
        >
          <span className="flex-shrink-0">{collapsed ? <IconExpand /> : <IconCollapse />}</span>
          {!collapsed && <span className="text-[14px] font-medium">{collapsed ? t('customerPortal.sidebar.pin') : t('customerPortal.sidebar.unpin')}</span>}
          {collapsed && (
            <div className="fixed left-16 ml-2 px-2.5 py-1.5 rounded-[10px] bg-[var(--bg-elevated)] border border-[var(--border-card)] text-xs text-[var(--text-primary)] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-dropdown">
              {t('customerPortal.sidebar.pin')}
            </div>
          )}
        </button>
      </div>
    </aside>
  );
}
